import React, { useContext } from 'react';
import { useRouter } from 'next/router';
import Link from 'next/link';
import { withRouter, WithRouterProps } from 'react-router';
import featureFlags from 'util/featureFlags';

// Types
import type { WPublicNavItemView } from '@zola/svc-web-api-ts-client';
import { WEBSITE_PAGE_TYPES } from 'components/manage/website/util/pageTypes';

// Context
import { useEditWebsiteActions } from 'components/manage/EditWebsite/context';
import { ThemeContext } from '../../../context';

// Utils
import { fillFontProperties } from '../../../util/mappers';
import getPublicWebsiteHref from '../../../util/getPublicWebsiteHref';

// Styles
import {
  NavItem,
  NavLinkContainer,
  NavContainer,
  NextLinkNavItem,
  AnimatedNavItem,
  AnimatedNextLinkNavItem,
} from './NavigationMenu.styles';

export type NavigationMenuProps = {
  className?: string;
  navItems?: WPublicNavItemView[];
  slug?: string;
};

const NavigationMenu = ({
  className,
  navItems,
  params,
  slug,
}: NavigationMenuProps & WithRouterProps): JSX.Element => {
  const {
    wedding,
    components: { globalBodyFontValues },
    inPreview,
  } = useContext(ThemeContext);
  const {
    state: { activePage },
  } = useEditWebsiteActions();

  const router = useRouter();
  const { pageType = 'home' } = router?.query || {}; // next; Public
  const { pageKey = activePage?.type?.toLowerCase() || 'home' } = params || {}; // react-router; inPreview
  const { public_theme_v2 } = wedding || {};
  const navBodyFontStyles = public_theme_v2?.components?.NAV?.fonts?.BODY?.[1];
  const navBodyFontStylesFinal = fillFontProperties(globalBodyFontValues, navBodyFontStyles);

  let isManageEvents: boolean | undefined;
  if (typeof window !== 'undefined') {
    isManageEvents = window?.location?.href?.includes('/wedding/manage/events');
  }

  const getActiveNavItem = (typeLower: string): boolean => {
    if (inPreview && isManageEvents) {
      return WEBSITE_PAGE_TYPES.EVENT.toLowerCase() === typeLower;
    }
    return (inPreview ? pageKey : pageType) === typeLower;
  };

  return (
    <NavContainer className={className} inPreview={inPreview}>
      <NavLinkContainer>
        {(navItems || []).map(({ type, nav_title, id }) => {
          const typeLower = ((type as unknown) as string).toLowerCase();
          if (!type) return null;
          if (inPreview) {
            return featureFlags.get('publicHomeAnimations') ? (
              <AnimatedNavItem
                key={id}
                linkStyles={navBodyFontStylesFinal}
                active={getActiveNavItem(typeLower)}
                href={getPublicWebsiteHref(slug, typeLower, router?.query)}
              >
                {nav_title}
              </AnimatedNavItem>
            ) : (
              <NavItem
                key={id}
                linkStyles={navBodyFontStylesFinal}
                active={getActiveNavItem(typeLower)}
                href={getPublicWebsiteHref(slug, typeLower, router?.query)}
              >
                {nav_title}
              </NavItem>
            );
          }
          return featureFlags.get('publicHomeAnimations') ? (
            <AnimatedNextLinkNavItem
              key={id}
              linkStyles={navBodyFontStylesFinal}
              active={getActiveNavItem(typeLower)}
            >
              <Link href={getPublicWebsiteHref(slug, typeLower, router?.query)}>{nav_title}</Link>
            </AnimatedNextLinkNavItem>
          ) : (
            <NextLinkNavItem
              key={id}
              linkStyles={navBodyFontStylesFinal}
              active={getActiveNavItem(typeLower)}
            >
              <Link href={getPublicWebsiteHref(slug, typeLower, router?.query)}>{nav_title}</Link>
            </NextLinkNavItem>
          );
        })}
      </NavLinkContainer>
    </NavContainer>
  );
};

export default withRouter(NavigationMenu);
